import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {FillLayerSpecification, LineLayerSpecification} from "mapbox-gl";
import Map, {Layer, MapRef, NavigationControl, ScaleControl, Source} from "react-map-gl";
import {useAtomValue} from "jotai";
import * as turf from "@turf/turf";
import {FeatureCollection, MultiPolygon, Polygon} from "geojson";
import {Field} from "../../types/domain/field";
import {mapTokenLoadable} from "../../store/mapTokenStore";
import {useFieldPopup} from "../../hooks/fieldPopupHooks";
import {MapPopup} from "../map/MapPopup";

interface Props {
    geojson: FeatureCollection<MultiPolygon | Polygon>;
    fields?: Field[];
}

export const OrderMap: React.FC<Props> = ({geojson, fields}) => {
    const {t} = useTranslation();
    const mapToken = useAtomValue(mapTokenLoadable);
    const mapRef = useRef<MapRef>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [viewState, setViewState] = useState({
        zoom: 7,
    });
    useEffect(() => {
        if (mapRef !== null && mapRef !== undefined && mapRef.current && mapLoaded) {
            // Helps to debug
            // window.globalMap = mapRef.current.getMap();

            if (geojson.features.length > 0) {
                const [minLng, minLat, maxLng, maxLat] = turf.bbox(geojson);
                mapRef.current.fitBounds(
                    [
                        [minLng, minLat],
                        [maxLng, maxLat],
                    ],
                    {padding: 40, duration: 0},
                );
            }
        }
    }, [mapLoaded, geojson]);

    const {onHover, onLeave, popupDetails} = useFieldPopup(fields, "popup-fill-layer", null);

    return (
        <>
            {mapToken.state === "hasData" && (
                <Map
                    style={{minHeight: 600}}
                    reuseMaps
                    ref={mapRef}
                    {...viewState}
                    onMove={(evt) => setViewState(evt.viewState)}
                    onLoad={() => setMapLoaded(true)}
                    mapStyle="mapbox://styles/mapbox/satellite-v9"
                    mapboxAccessToken={mapToken.data}
                    interactiveLayerIds={["popup-fill-layer"]}
                    onMouseMove={onHover}
                    onMouseLeave={onLeave}
                >
                    {geojson && (
                        <>
                            <Source type="geojson" data={geojson}>
                                <Layer {...layerStyle} />
                                <Layer {...layerStyleFill} />
                            </Source>
                        </>
                    )}
                    {popupDetails && <MapPopup details={popupDetails} t={t} />}
                    <ScaleControl />
                    <NavigationControl />
                </Map>
            )}
        </>
    );
};

const layerStyle: LineLayerSpecification = {
    id: "borders-layer",
    type: "line",
    source: "borders-source",
    paint: {
        "line-color": "gray",
        "line-width": 2,
        "line-opacity": 1,
    },
};
//We need fill layer for the popup to work.
const layerStyleFill: FillLayerSpecification = {
    id: "popup-fill-layer",
    source: "popup-fill-source",
    type: "fill",
    paint: {
        "fill-color": "#3288bd",
        "fill-opacity": 0.6,
    },
};
